// UX & CRO Assessment — Results Page JS
// Reads JSON from #ux-report-json and renders the full dashboard

function initUxResults() {
  const payload = document.querySelector("#ux-report-json");
  if (!payload) return;

  const encodedReport = payload.dataset.report || "";
  if (!encodedReport) return;

  let data;
  try {
    data = JSON.parse(decodeBase64Utf8(encodedReport));
    if (typeof data === "string") data = JSON.parse(data);
  } catch {
    return;
  }

  if (!data || typeof data !== "object") return;

  renderUxDashboard(data);
}

function decodeBase64Utf8(encodedValue) {
  const binary = window.atob(encodedValue);
  const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
  return new TextDecoder().decode(bytes);
}

// ── Dashboard ───────────────────────────────────────────────────────────────

function renderUxDashboard(data) {
  const dd      = data.dashboard_data  || {};
  const summary = dd.summary           || {};
  const charts  = dd.charts            || {};

  // Score gauge
  drawUxGauge("ux-score-gauge", summary.score ?? 0, summary.score_color ?? "red");

  // Badges
  const issuesEl = document.querySelector("#ux-badge-issues");
  const critEl   = document.querySelector("#ux-badge-critical");
  if (issuesEl) issuesEl.textContent = `${summary.total_issues ?? "—"} problemi totali`;
  if (critEl)   critEl.textContent   = `${summary.critical_issues ?? "—"} critici`;

  // Charts
  if (charts.severity_distribution) drawUxPie("ux-chart-severity", charts.severity_distribution);
  if (charts.heuristic_violations)  drawUxBar("ux-chart-violations", charts.heuristic_violations);

  // Critical issues table
  renderUxCriticalTable(dd.top_3_critical_issues || []);

  // Issues accordion (failed rules only)
  renderUxAccordion(data.issues_detected || []);

  // Positive summary
  renderUxPositiveSummary(data.positive_summary || "");

  // Strategic plan
  renderUxPlan(data.strategic_action_plan || []);
}

// ── Circular gauge ──────────────────────────────────────────────────────────

const UX_COLOR_MAP = { green: "#00c36f", orange: "#ff7517", red: "#e63131" };

function drawUxGauge(containerId, score, colorKey) {
  const wrap   = document.getElementById(containerId);
  if (!wrap) return;

  const size  = 160;
  const cx    = size / 2, cy = size / 2;
  const r     = 62, sw = 12;
  const circ  = 2 * Math.PI * r;
  const color = UX_COLOR_MAP[colorKey] || UX_COLOR_MAP.red;
  const targetOffset = circ - (score / 100) * circ;

  wrap.innerHTML = `
    <svg viewBox="0 0 ${size} ${size}" width="${size}" height="${size}" style="display:block">
      <circle cx="${cx}" cy="${cy}" r="${r}" fill="none"
        stroke="rgba(255,255,255,0.08)" stroke-width="${sw}"/>
      <circle class="ux-gauge-arc" cx="${cx}" cy="${cy}" r="${r}" fill="none"
        stroke="${color}" stroke-width="${sw}" stroke-linecap="round"
        stroke-dasharray="${circ}" stroke-dashoffset="${circ}"
        style="transform-box:fill-box;transform-origin:center;transition:stroke-dashoffset 1.4s cubic-bezier(0.22,1,0.36,1)"
        data-target="${targetOffset}"/>
      <text x="${cx}" y="${cy + 14}" text-anchor="middle"
        font-family="Area Inktrap Extrablack, Impact, sans-serif"
        font-size="48" font-weight="400" fill="${color}">${score}</text>
      <text x="${cx}" y="${cy + 32}" text-anchor="middle"
        font-family="Exo, sans-serif"
        font-size="9" font-weight="700" fill="rgba(255,255,255,0.35)"
        letter-spacing="2" text-transform="uppercase">/ 100</text>
    </svg>`;

  const arc = wrap.querySelector(".ux-gauge-arc");
  if (arc) {
    requestAnimationFrame(() => requestAnimationFrame(() => {
      arc.style.strokeDashoffset = targetOffset;
    }));
  }
}

// ── Donut pie chart ─────────────────────────────────────────────────────────

function drawUxPie(containerId, chartData) {
  const el = document.getElementById(containerId);
  if (!el) return;

  const labels = chartData.labels || [];
  const values = chartData.data   || [];
  const total  = values.reduce((a, b) => a + b, 0);

  if (total === 0) {
    el.innerHTML = '<p style="color:#999;font-size:.8rem;text-align:center;padding:2rem 0">Nessun problema rilevato</p>';
    return;
  }

  const COLORS = ["#e63131", "#ff7517", "#f5c518", "#cccccc"];
  const size = 160, cx = size / 2, cy = size / 2, r = 60, innerR = 26;

  let paths = "", legend = "";
  let startAngle = -Math.PI / 2;

  values.forEach((val, i) => {
    const color = COLORS[i % COLORS.length];
    if (!val) {
      legend += `<li style="opacity:.4"><span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${color};margin-right:6px;vertical-align:middle"></span>${escUxHtml(labels[i] || "")} <strong>0</strong></li>`;
      return;
    }
    const slice = (val / total) * 2 * Math.PI;
    const end   = startAngle + slice;
    const x1 = cx + r * Math.cos(startAngle), y1 = cy + r * Math.sin(startAngle);
    const x2 = cx + r * Math.cos(end),         y2 = cy + r * Math.sin(end);
    const xi1 = cx + innerR * Math.cos(end),   yi1 = cy + innerR * Math.sin(end);
    const xi2 = cx + innerR * Math.cos(startAngle), yi2 = cy + innerR * Math.sin(startAngle);
    const large = slice > Math.PI ? 1 : 0;
    paths  += `<path d="M${x1},${y1} A${r},${r} 0 ${large},1 ${x2},${y2} L${xi1},${yi1} A${innerR},${innerR} 0 ${large},0 ${xi2},${yi2} Z" fill="${color}" opacity="0.92"/>`;
    legend += `<li><span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${color};margin-right:6px;vertical-align:middle;flex-shrink:0"></span>${escUxHtml(labels[i] || "")} <strong>${val}</strong></li>`;
    startAngle = end;
  });

  el.innerHTML = `
    <div style="display:flex;gap:1.5rem;align-items:center;flex-wrap:wrap">
      <svg viewBox="0 0 ${size} ${size}" width="${size}" height="${size}" style="flex-shrink:0">
        ${paths}
        <circle cx="${cx}" cy="${cy}" r="${innerR}" fill="#fff"/>
      </svg>
      <ul style="list-style:none;padding:0;margin:0;font-size:.78rem;line-height:2.1;color:#0a0a0a;font-family:Exo,sans-serif;display:flex;flex-direction:column;gap:0">${legend}</ul>
    </div>`;
}

// ── Bar chart ───────────────────────────────────────────────────────────────

function drawUxBar(containerId, chartData) {
  const el = document.getElementById(containerId);
  if (!el) return;

  const labels = chartData.labels || [];
  const values = chartData.data   || [];
  const maxVal = Math.max(...values, 1);
  const COLORS = ["#1400ff", "#ff7517", "#00c36f", "#e63131"];

  el.innerHTML = `<div style="padding:.25rem 0">${
    values.map((v, i) => {
      const color = COLORS[i % COLORS.length];
      return `
        <div style="margin-bottom:.9rem">
          <div style="display:flex;justify-content:space-between;align-items:baseline;margin-bottom:.3rem">
            <span style="font-size:.68rem;font-weight:800;text-transform:uppercase;letter-spacing:.07em;font-family:Exo,sans-serif;color:#0a0a0a">${escUxHtml(labels[i] || "")}</span>
            <span style="font-size:.9rem;font-weight:800;font-family:Exo,sans-serif;color:${color}">${v}</span>
          </div>
          <div style="height:8px;background:#f0f0f0;overflow:hidden">
            <div class="ux-bar-fill" data-pct="${(v / maxVal) * 100}"
                 style="height:100%;width:0;background:${color};transition:width 1.1s cubic-bezier(0.22,1,0.36,1)"></div>
          </div>
        </div>`;
    }).join("")
  }</div>`;

  requestAnimationFrame(() => {
    el.querySelectorAll(".ux-bar-fill").forEach(bar => {
      requestAnimationFrame(() => { bar.style.width = `${bar.dataset.pct}%`; });
    });
  });
}

// ── Critical issues table ───────────────────────────────────────────────────

function renderUxCriticalTable(issues) {
  const tbody = document.querySelector("#ux-critical-body");
  if (!tbody) return;

  tbody.innerHTML = issues.map(issue => {
    const sev = (issue.severity || "").toLowerCase();
    return `
      <tr>
        <td>${escUxHtml(issue.issue || "")}</td>
        <td><span class="ux-severity-badge ux-severity-${sev}">${escUxHtml(issue.severity || "")}</span></td>
      </tr>`;
  }).join("");
}

// ── Issues accordion (failed rules only) ────────────────────────────────────

function renderUxAccordion(issues) {
  const container = document.querySelector("#ux-accordion-body");
  if (!container) return;

  if (!issues.length) {
    container.innerHTML = '<p style="color:#999;font-size:.85rem;text-align:center;padding:2rem 0">Nessun problema rilevato.</p>';
    return;
  }

  container.innerHTML = issues.map(rule => `
      <div class="ux-rule">
        <div class="ux-rule-header" tabindex="0" role="button" aria-expanded="false">
          <span class="ux-rule-id">${String(rule.rule_id ?? "").padStart(2, "0")}</span>
          <span class="ux-rule-name">${escUxHtml(rule.rule_name || "")}</span>
          <span class="ux-rule-category-tag">${escUxHtml(rule.category || "")}</span>
          <span class="ux-status-dot ux-dot--fail" title="Fail"></span>
          <span class="ux-rule-chevron" aria-hidden="true"></span>
        </div>
        <div class="ux-rule-body">
          <p class="ux-rule-explanation">${escUxHtml(rule.explanation || "")}</p>
        </div>
      </div>`).join("");

  container.querySelectorAll(".ux-rule-header").forEach(header => {
    const rule = header.closest(".ux-rule");
    function toggle() {
      const open = rule.classList.toggle("ux-rule--open");
      header.setAttribute("aria-expanded", String(open));
    }
    header.addEventListener("click", toggle);
    header.addEventListener("keypress", e => {
      if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggle(); }
    });
  });
}

// ── Positive summary ─────────────────────────────────────────────────────────

function renderUxPositiveSummary(text) {
  const el = document.querySelector("#ux-positive-summary");
  if (!el || !text) return;
  el.textContent = text;
  el.closest(".ux-positive-section")?.removeAttribute("hidden");
}

// ── Strategic plan ──────────────────────────────────────────────────────────

function renderUxPlan(plan) {
  const list = document.querySelector("#ux-plan-list");
  if (!list) return;

  list.innerHTML = plan.map(item =>
    `<li><span class="ux-plan-text">${escUxHtml(item)}</span></li>`
  ).join("");
}

// ── Utility ─────────────────────────────────────────────────────────────────

function escUxHtml(str) {
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

document.addEventListener("turbo:load", initUxResults);
